import { api } from "@/api/client";
import { PanelHelp } from "@/components/PanelHelp";
import { Staleness } from "@/components/Staleness";
import { useECharts } from "@/hooks/useECharts";
import { usePoll } from "@/hooks/usePoll";
import { t } from "@/i18n";
import { formatDecimal, formatInt, modeLabel, tenantLabel } from "@/lib/format";
import { includeTestSessions, selectedTenant, selectedWindow } from "@/state/filters";

export function PulseView() {
  const { data, loading, error, lastFetched, refetch } = usePoll(
    (s) =>
      api.snapshot(
        {
          window: selectedWindow.value,
          includeTest: includeTestSessions.value,
          tenant: selectedTenant.value,
        },
        s,
      ),
    [selectedWindow.value, includeTestSessions.value, selectedTenant.value],
  );

  const tenants = Object.entries(data?.tenant_distribution ?? {}).sort(
    (a, b) => b[1] - a[1],
  );

  const modeKickoffOption = data
    ? {
        title: { text: t("pulse.chart.modeKickoff"), left: "center", textStyle: { fontSize: 14 } },
        tooltip: { trigger: "item" },
        series: [
          {
            type: "pie",
            radius: ["35%", "65%"],
            label: { formatter: "{b}\n{c}" },
            data: Object.entries(data.mode_kickoff).map(([mode, count]) => ({
              name: modeLabel(mode),
              value: count,
            })),
          },
        ],
      }
    : null;

  const modeKickoffRef = useECharts(modeKickoffOption);

  const localeOption = data ? barChart(t("pulse.chart.locale"), data.locale_distribution) : null;
  const localeRef = useECharts(localeOption);

  const deviceOption = data ? barChart(t("pulse.chart.device"), data.device_distribution) : null;
  const deviceRef = useECharts(deviceOption);

  const countryOption = data
    ? barChart(t("pulse.chart.country"), data.country_distribution)
    : null;
  const countryRef = useECharts(countryOption);

  return (
    <div style="display: grid; gap: 24px;">
      <div style="display: flex; align-items: center; gap: 24px;">
        <Staleness
          lastFetched={lastFetched}
          loading={loading}
          error={error}
          onRefresh={refetch}
        />
      </div>

      <section class="card" style="padding-bottom: var(--space-3);">
        <PanelHelp k="pulse.active" />
        <div class="grid grid-3">
          <KpiCard
            label={t("pulse.active5m")}
            value={formatInt(data?.active_sessions_5m ?? 0)}
            sub={t("pulse.turnsSub", { n: formatInt(data?.turns_5m ?? 0) })}
          />
          <KpiCard
            label={t("pulse.active1h")}
            value={formatInt(data?.active_sessions_1h ?? 0)}
            sub={t("pulse.turnsSub", { n: formatInt(data?.turns_60m ?? 0) })}
          />
          <KpiCard
            label={t("pulse.active24h")}
            value={formatInt(data?.active_sessions_24h ?? 0)}
            sub={t("pulse.avgQuestions", { n: formatDecimal(data?.avg_question_count ?? 0) })}
          />
        </div>
      </section>

      <section class="card">
        <h2>{t("pulse.modeKickoff")}</h2>
        <PanelHelp k="pulse.modeKickoff" />
        <div class="grid grid-3">
          <div ref={modeKickoffRef} class="chart" />
          <div>
            <h3>{t("pulse.topTransitions")}</h3>
            <table class="table">
              <thead>
                <tr>
                  <th>{t("pulse.from")}</th>
                  <th>{t("pulse.to")}</th>
                  <th>{t("common.count")}</th>
                </tr>
              </thead>
              <tbody>
                {data?.mode_transitions.slice(0, 10).map((tr) => (
                  <tr key={`${tr.from_mode}-${tr.to_mode}`}>
                    <td>{modeLabel(tr.from_mode)}</td>
                    <td>{modeLabel(tr.to_mode)}</td>
                    <td>{formatInt(tr.count)}</td>
                  </tr>
                ))}
                {(!data || data.mode_transitions.length === 0) && (
                  <tr>
                    <td colspan={3} class="muted">
                      {t("pulse.noTransitions")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div>
            <h3>{t("pulse.origins")}</h3>
            <PanelHelp k="pulse.origins" />
            {/* Partner first, raw hosts underneath: the tenant is the unit a
                commercial question is asked in, the host is the drill-down. */}
            {tenants.length > 0 && (
              <table class="table" style="margin-bottom: var(--space-3);">
                <thead>
                  <tr>
                    <th>{t("header.partnerGroup")}</th>
                    <th>{t("common.sessions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.map(([tenant, n]) => (
                    <tr key={tenant}>
                      <td>{tenantLabel(tenant)}</td>
                      <td>{formatInt(n)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <table class="table">
              <thead>
                <tr>
                  <th>{t("pulse.embeddingHost")}</th>
                  <th>{t("common.sessions")}</th>
                </tr>
              </thead>
              <tbody>
                {data &&
                  Object.entries(data.origin_distribution)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 10)
                    .map(([origin, n]) => (
                      <tr key={origin}>
                        <td style="word-break: break-all;">{origin}</td>
                        <td>{formatInt(n)}</td>
                      </tr>
                    ))}
                {(!data || Object.keys(data.origin_distribution).length === 0) && (
                  <tr>
                    <td colspan={2} class="muted">
                      {t("pulse.noOrigins")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section class="card">
        <h2>{t("pulse.audience")}</h2>
        <PanelHelp k="pulse.audience" />
        <div class="grid grid-3">
          <div ref={localeRef} class="chart" />
          <div ref={countryRef} class="chart" />
          <div ref={deviceRef} class="chart" />
        </div>
      </section>
    </div>
  );
}

function barChart(title: string, dist: Record<string, number>) {
  const entries = Object.entries(dist).sort((a, b) => b[1] - a[1]);
  return {
    title: { text: title, left: "center", textStyle: { fontSize: 14 } },
    tooltip: { trigger: "axis" },
    xAxis: { type: "category", data: entries.map(([k]) => k) },
    yAxis: { type: "value" },
    series: [
      {
        type: "bar",
        data: entries.map(([, v]) => v),
        itemStyle: { color: "#002da5" },
      },
    ],
  };
}

function KpiCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div class="card" style="border-left: 4px solid var(--brand-blue);">
      <h3>{label}</h3>
      <div class="kpi">{value}</div>
      {sub && <div class="kpi-sub">{sub}</div>}
    </div>
  );
}
